import { documentHasElements } from "../../modules/dom";

/**
 * Validates if a string is in valid slug format (lowercase, hyphen-separated, no special characters).
 * Only allows: lowercase letters (a-z), digits (0-9), and hyphens (-).
 * Rejects: spaces, uppercase letters, underscores, encoded characters, and any special characters.
 */
function isValidSlug(str: string): boolean {
    return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(str);
}

/**
 * Gets the current URL hash without the leading '#' character.
 * Returns the RAW hash without decoding or case conversion to preserve original format for validation.
 */
function getUrlHash(): string {
    // Use window.location.href to get the raw URL and extract hash manually
    // This preserves encoded characters like %20 and uppercase letters for proper validation
    const href = window.location.href;
    const hashIndex = href.indexOf('#');
    if (hashIndex === -1) {
        return '';
    }
    return href.substring(hashIndex + 1);
}

/**
 * Updates the URL hash without triggering a page scroll.
 */
function updateUrlHash(hash: string): void {
    if (history.pushState) {
        history.pushState(null, '', `#${hash}`);
    } else {
        window.location.hash = hash;
    }
}

let tabContainerCounter = 0;

documentHasElements(".js-tab-container-block", (tabContainerBlocks) => {

    tabContainerBlocks.forEach((tabContainerBlock) => {
        const blocks = tabContainerBlock.querySelectorAll(".tab-blocks > div");
        const tabs = tabContainerBlock.querySelector(".tab-list");
        const images = tabContainerBlock.querySelector(".tab-images");

        // Store slug mappings for anchor link functionality
        const slugToIndex = new Map<string, number>();

        // Generate unique ID prefix for this tab container (deterministic counter instead of Math.random)
        const tabContainerId = tabContainerBlock.getAttribute("id") || `tab-container-${tabContainerCounter++}`;

        // Create list items for each block
        blocks.forEach((block, index) => {
            block.setAttribute("data-item", index.toString());
            // Make tab content focusable for keyboard navigation
            block.setAttribute("tabindex", "0");
            const listItem = document.createElement("li");
            const blockName = block.getAttribute("data-block-name");
            const anchorId = block.getAttribute("data-anchor-id");
            const image = block.querySelector(".col.image")

            // Generate unique IDs for ARIA relationships
            const tabId = `${tabContainerId}-tab-${index}`;
            const panelId = block.getAttribute("id") || `${tabContainerId}-panel-${index}`;

            listItem.setAttribute("data-item", index.toString());
            listItem.setAttribute("id", tabId);

            // Add keyboard accessibility attributes
            listItem.setAttribute("role", "tab");
            listItem.setAttribute("tabindex", "0");
            listItem.setAttribute("aria-selected", index === 0 ? "true" : "false");
            listItem.setAttribute("aria-controls", panelId);

            // Add aria-labelledby to the panel pointing back to the tab
            block.setAttribute("aria-labelledby", tabId);

            if (blockName !== null) {
                listItem.textContent = blockName.toString();
            }

            // Store only the slug for anchor link matching (slug is provided by server-side ToSlug function via data-anchor-id)
            // Only valid slug format (lowercase, hyphen-separated) will be accepted for navigation
            if (anchorId) {
                listItem.setAttribute("data-anchor-id", anchorId);
                slugToIndex.set(anchorId, index);
            }

            if (listItem && tabs) {
                tabs.appendChild(listItem);
            }

            if (images && image) {
                image.setAttribute("image-item", index.toString());
                const clone = image.cloneNode(true)
                image.removeAttribute("image-item");

                images.appendChild(clone);
            }
        });

        const tabButtons = tabContainerBlock.querySelectorAll(".tab-list li");

        // Function to activate a tab by index
        function activateTab(index: number, shouldUpdateHash: boolean = false, shouldFocus: boolean = false): void {
            const tab = tabButtons[index];
            if (!tab) return;

            const tabId = tab.getAttribute("data-item");
            const tabContent = tabContainerBlock.querySelector(`div[data-item="${tabId}"]`);
            const tabImage = tabContainerBlock.querySelector(`div[image-item="${tabId}"]`);

            // Close all tabs and update aria-selected
            tabButtons.forEach((otherTab) => {
                otherTab.classList.remove("active");
                otherTab.setAttribute("aria-selected", "false");
            });
            blocks.forEach((block) => {
                block.classList.remove('active');
            });
            images?.querySelector(".image.active")?.classList.remove('active');

            // Open the specified tab
            tab.classList.add("active");
            tab.setAttribute("aria-selected", "true");
            tabContent?.classList.add('active');
            tabImage?.classList.add('active');

            // Set focus on the tab content if requested (keyboard navigation)
            if (shouldFocus && tabContent instanceof HTMLElement) {
                // Temporarily make focusable for keyboard navigation
                tabContent.setAttribute('tabindex', '-1');
                tabContent.focus();
                // Remove tabindex after blur to prevent URL anchor from focusing
                tabContent.addEventListener('blur', function removeTabIndex() {
                    tabContent.removeAttribute('tabindex');
                    tabContent.removeEventListener('blur', removeTabIndex);
                }, { once: true });
            }

            // Update URL hash if requested
            if (shouldUpdateHash) {
                const tabAnchorId = tab.getAttribute("data-anchor-id");
                if (tabAnchorId) {
                    updateUrlHash(tabAnchorId);
                }
            }

            // Handle mobile scroll
            if (document.documentElement.clientWidth < 800) {     
                const tabWrapper = tab.parentElement?.parentElement;

                if (tabWrapper != null) {
                    tabWrapper.scrollTo({
                        top: 0,
                        left: (tab as HTMLElement).offsetLeft - 15,
                        behavior: 'smooth'
                    });
                }
            }
        }

        // Function to find tab index by URL hash
        function findTabIndexByHash(hash: string): number {
            if (!hash) return -1;
            return slugToIndex.get(hash) ?? -1;
        }

        // Check for anchor in URL and activate corresponding tab
        const urlHash = getUrlHash();

        // Only process if the hash is in valid slug format (lowercase, hyphen-separated)
        const isValidAnchor = urlHash && isValidSlug(urlHash);
        const matchingTabIndex = isValidAnchor ? findTabIndexByHash(urlHash) : -1;

        if (matchingTabIndex >= 0) {
            // Valid slug anchor found - activate tab and scroll to it
            activateTab(matchingTabIndex, false, false);
            requestAnimationFrame(() => {
                tabContainerBlock.scrollIntoView({ behavior: 'smooth', block: 'start' });
                // Remove focus from any focused element to prevent focus outline on URL navigation
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
            });
        } else {
            // Invalid anchor or no anchor - show default first tab without scrolling
            tabButtons[0]?.classList.add('active');
            tabButtons[0]?.setAttribute("aria-selected", "true");
            blocks[0]?.classList.add('active');
            images?.children[0]?.classList.add('active');
        }

        // Handle tab click events
        tabButtons.forEach((tab, index) => {
            tab.addEventListener("click", () => {
                const isActive = tab.classList.contains("active");

                if (isActive) {
                    return
                }

                // Activate clicked tab and update URL hash
                activateTab(index, true, false);
            });

            // Handle keyboard events for accessibility
            (tab as HTMLElement).addEventListener("keydown", (event: KeyboardEvent) => {
                const currentIndex = index;
                const lastIndex = tabButtons.length - 1;
                let targetIndex: number;

                switch (event.key) {
                    case "Enter":
                    case " ": // Space
                        event.preventDefault();
                        if (!tab.classList.contains("active")) {
                            activateTab(currentIndex, true, true);
                        }
                        break;
                    case "ArrowRight":
                        event.preventDefault();
                        // Move to next tab, wrap to first if at end
                        targetIndex = currentIndex < lastIndex ? currentIndex + 1 : 0;
                        activateTab(targetIndex, true, true);
                        break;
                    case "ArrowLeft":
                        event.preventDefault();
                        // Move to previous tab, wrap to last if at beginning
                        targetIndex = currentIndex > 0 ? currentIndex - 1 : lastIndex;
                        activateTab(targetIndex, true, true);
                        break;
                    case "Home":
                        event.preventDefault();
                        // Move to first tab
                        activateTab(0, true, true);
                        break;
                    case "End":
                        event.preventDefault();
                        // Move to last tab
                        activateTab(lastIndex, true, true);
                        break;
                }
            });
        });

        // Handle browser back/forward navigation
        window.addEventListener("hashchange", () => {
            const newHash = getUrlHash();
            if (newHash && isValidSlug(newHash)) {
                const newTabIndex = findTabIndexByHash(newHash);
                if (newTabIndex >= 0) {
                    activateTab(newTabIndex, false, false);
                    // Remove focus from any focused element to prevent focus outline on URL navigation
                    requestAnimationFrame(() => {
                        if (document.activeElement instanceof HTMLElement) {
                            document.activeElement.blur();
                        }
                    });
                }
            }
        });
    });
});